import { useCallback, useEffect, useMemo } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Box } from '@mui/material';
import 'weave-timeline/styles.css';
import { PlaybackControls, Timeline, TimelineProvider, type CanvasMode, type Track } from 'weave-timeline';
import {
  useCompositorView,
  useFlowState,
  useNodeData,
  useVideoState,
  useVideoView,
} from '@/components/Recipe/FlowContext';
import { Flex, FlexCol } from '@/UI/styles';
import AppDivider from '@/UI/AppDivider/AppDivider';
import { color } from '@/colors';
import { FF_COMP_TIMELINE_DISPLAY } from '@/consts/featureFlags';
import { CompositorNodeV3 } from '@/types/nodes/compositor';
import { useCursor } from './useCursor';
import { useAudioPlayback } from './useAudioPlayback';
import { useResizeableTimeline } from './useResizeableTimeline';
import { LayerPanel } from './LayerPanel/LayerPanel';
import { DesignerEditorActionsBar } from './NavigationPanel';
import { CompositorCanvas } from './CompositorCanvas';
import { LayerPropertyPanel } from './LayerPropertyPanel/LayerPropertyPanel';
import type { NodeId } from 'web';

interface DesignerEditorProps {
  id: NodeId;
  setCleanup: (cleanup: () => Promise<void> | void) => void;
}

export function DesignerEditor({ id, setCleanup }: DesignerEditorProps) {
  const compositorView = useCompositorView(id);
  const {
    isTimelineEnabled,
    layerOrder,
    layers,
    input: compositorInputs,
    duration: compositorDuration,
    fps: compositorFps,
  } = useNodeData<CompositorNodeV3>(id).data;

  const cursor = useCursor(id);
  const selectedLayerId = useFlowState((s) => s.compositor[id]?.selectedLayer);
  const canvasZoom = useFlowState((s) => s.compositor[id]?.zoomLevel) ?? 1;

  const ff_isTimelineDisplayEnabled = useFeatureFlagEnabled(FF_COMP_TIMELINE_DISPLAY);

  const videoView = useVideoView(id);
  const videoState = useVideoState(id);

  useAudioPlayback({
    layers,
    layerOrder,
    compositorInputs,
    currentTime: videoState?.currentTime ?? 0,
    isPlaying: videoState !== undefined ? !videoState.paused : false,
    globalMuted: videoState?.muted ?? false,
  });

  const timelineTracks = useMemo<Track[]>(() => {
    if (!layerOrder || !layers) {
      return [];
    }

    return layerOrder
      .map((layerId) => {
        const layer = layers[layerId];
        if (!layer) {
          return null;
        }

        // Match compositor renderer: input connected
        const matchedInput = compositorInputs?.find(
          ([inputId, value]) => inputId === layer.kind.inputId && value != null,
        );
        if (!matchedInput) {
          return null;
        }

        const matchedValue = matchedInput[1];
        const file = matchedValue?.file;
        const thumbnailUrl =
          file && (file.type === 'image' || file.type === 'video') ? (file.thumbnailUrl ?? file.url) : undefined;
        const label = layer.kind.type === 'text' ? matchedValue?.string : undefined;
        return {
          id: layerId,
          name: layer.name,
          type: layer.kind.type,
          locked: layer.locked,
          hidden: !layer.visible,
          muted: layer.kind.type === 'video' || layer.kind.type === 'audio' ? layer.kind.muted : undefined,
          thumbnailUrl,
          label,
          duration: layer.duration ?? compositorDuration,
          startTime: layer.startTime ?? 0,
          trimStart:
            layer.kind.type === 'video' || layer.kind.type === 'audio' ? (layer.kind.trimStart ?? 0) : undefined,
          trimEnd: layer.kind.type === 'video' || layer.kind.type === 'audio' ? (layer.kind.trimEnd ?? 0) : undefined,
        } as Track;
      })
      .filter((t): t is Track => t !== null)
      .reverse();
  }, [layerOrder, layers, compositorInputs, compositorDuration]);

  const { timelineH, handleTimelineResizeMouseDown } = useResizeableTimeline({
    nodeId: id,
    layerCount: timelineTracks.length,
  });

  // Timeline actions → compositor video
  const handlePlayingChange = useCallback(() => {
    void videoView.togglePlay();
  }, [videoView]);

  const handleSeek = useCallback(
    (time: number) => {
      void videoView.setTime(time, false);
    },
    [videoView],
  );

  const handleToggleMute = useCallback(() => {
    void videoView.toggleMute();
  }, [videoView]);

  const handleCanvasModeChange = useCallback(
    (mode: CanvasMode) => {
      compositorView.setToolMode(mode === 'select' ? 'selection' : 'pan');
    },
    [compositorView],
  );

  const onCanvasZoomChanged = useCallback(
    (zoom: number) => {
      compositorView.setZoomLevel(zoom);
    },
    [compositorView],
  );

  const onZoomToFit = useCallback(() => {
    compositorView.zoomToFit();
  }, [compositorView]);

  const handleTrackLockToggle = useCallback(
    (trackId: number, locked: boolean) => {
      compositorView.setIsLayerLocked(trackId, locked);
    },
    [compositorView],
  );

  const handleTrackMuteToggle = useCallback(
    (trackId: number, muted: boolean) => {
      compositorView.setLayerMuted(trackId, muted);
    },
    [compositorView],
  );

  const handleTrackVisibilityToggle = useCallback(
    (trackId: number, hidden: boolean) => {
      compositorView.setLayerVisible(trackId, !hidden);
    },
    [compositorView],
  );

  const handleTrackStartTimeChange = useCallback(
    (trackId: number, startTime: number, ongoing: boolean) => {
      compositorView.setLayerStartTime(trackId, startTime, ongoing);
    },
    [compositorView],
  );

  const handleTrackSelect = useCallback(
    (trackId: number | null) => {
      compositorView.setSelectedLayerId(trackId ?? undefined);
    },
    [compositorView],
  );

  const handleTrackReorder = useCallback(
    (sourceLayerId: number, destinationLayerId: number) => {
      const toIndex = layerOrder.indexOf(destinationLayerId);
      if (toIndex === -1) return;
      compositorView.setLayerOrder(sourceLayerId, toIndex);
    },
    [compositorView, layerOrder],
  );

  const handleTrackTrimChange = useCallback(
    (
      trackId: number,
      updates: { startTime: number; duration: number; trimStart?: number; trimEnd?: number },
      ongoing: boolean,
    ) => {
      compositorView.setLayerTrim(trackId, updates, ongoing);
    },
    [compositorView],
  );

  const handleClose = useCallback(() => {
    compositorView.setSelectedLayerId(undefined);
    compositorView.disableNodeEditor();
    compositorView.disableUndoBarrier();
    compositorView.renderResult();
  }, [compositorView]);

  // On exit, clear the editor
  // TODO: Move this logic when moving the editor to a new component
  useEffect(() => {
    setCleanup(handleClose);
  }, [handleClose, setCleanup]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e instanceof KeyboardEvent) {
        const isCtrl = e.getModifierState('Control') || e.getModifierState('Meta');
        if (isCtrl && (e.key === 'z' || e.key === 'Z')) {
          if (e.getModifierState('Shift')) {
            compositorView.redo();
          } else {
            compositorView.undo();
          }
        }

        // if (e.key === 'Backspace' || e.key === 'Delete') {
        //   const selectedLayerIds = compositorView.selectedLayersIds();
        //   if (selectedLayerIds.length > 0) {
        //     compositorView.removeLayers(id, selectedLayerIds);
        //   }
        // }

        if ((isCtrl && e.key === '=') || e.key === '+') {
          const zoomLevel = compositorView.zoomLevel();
          onCanvasZoomChanged(zoomLevel * 1.1);
          e.preventDefault();
        }

        if (isCtrl && e.key === '-') {
          const zoomLevel = compositorView.zoomLevel();
          onCanvasZoomChanged(zoomLevel / 1.1);
          e.preventDefault();
        }

        if (isCtrl && e.key === '0') {
          onCanvasZoomChanged(1);
          e.preventDefault();
        }

        if (isCtrl && e.key === '1') {
          onZoomToFit();
          e.preventDefault();
        }

        // Arrow keys: nudge selected layer on canvas
        const target = e.target as HTMLElement;
        const isEditable = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
        if (!isCtrl && !isEditable && selectedLayerId != null) {
          const selectedLayer = compositorView.getLayer(selectedLayerId);
          if (selectedLayer && !selectedLayer.locked) {
            const DISTANCE = e.shiftKey ? 10 : 1;
            switch (e.key) {
              case 'ArrowUp':
                compositorView.setLayerPositionY(selectedLayerId, selectedLayer.position.y + DISTANCE);
                e.preventDefault();
                break;
              case 'ArrowDown':
                compositorView.setLayerPositionY(selectedLayerId, selectedLayer.position.y - DISTANCE);
                e.preventDefault();
                break;
              case 'ArrowLeft':
                compositorView.setLayerPositionX(selectedLayerId, selectedLayer.position.x - DISTANCE);
                e.preventDefault();
                break;
              case 'ArrowRight':
                compositorView.setLayerPositionX(selectedLayerId, selectedLayer.position.x + DISTANCE);
                e.preventDefault();
                break;
            }
          }
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [compositorView, onCanvasZoomChanged, onZoomToFit, selectedLayerId]);

  const toolMode = useFlowState((state) => state.compositor[id]?.toolMode);
  const CURSOR_STYLE = toolMode === 'pan' ? { cursor: 'grab' } : {};
  const canvasMode: CanvasMode = toolMode === 'selection' ? 'select' : 'pan';

  return (
    <FlexCol sx={{ flex: 1, height: '100%', position: 'relative' }}>
      <Flex
        sx={{
          flex: 1,
          position: 'absolute',
          inset: `0 0 ${ff_isTimelineDisplayEnabled && isTimelineEnabled ? timelineH : 0}px 0`,
          justifyContent: 'space-between',
          alignItems: 'stretch',
          width: '100%',
          userSelect: 'none',
          pointerEvents: 'none',
          '& > *': {
            pointerEvents: 'auto',
          },
          cursor,
        }}
      >
        <LayerPanel nodeId={id} />

        <FlexCol sx={{ height: '100%', width: '100%', ...CURSOR_STYLE }}>
          <CompositorCanvas />
          {(!ff_isTimelineDisplayEnabled || !isTimelineEnabled) && <DesignerEditorActionsBar nodeId={id} />}
        </FlexCol>

        <LayerPropertyPanel nodeId={id} />
      </Flex>
      {ff_isTimelineDisplayEnabled && isTimelineEnabled && (
        <>
          <Box
            sx={{
              height: 12,
              cursor: 'ns-resize',
              position: 'absolute',
              bottom: timelineH - 2,
              width: '100%',
              zIndex: 1,
              '&:hover hr': {
                borderColor: color.White40_T,
              },
            }}
            onMouseDown={handleTimelineResizeMouseDown}
          >
            <AppDivider
              sx={{
                width: '100%',
                position: 'absolute',
                bottom: 2,
                height: 4,
                flexShrink: 0,
                transition: 'border-color 0.15s ease',
              }}
            />
          </Box>
          <Box
            sx={{
              height: timelineH,
              flexShrink: 0,
              position: 'absolute',
              inset: 'auto 0 0 0',
              background: color.Black92,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <TimelineProvider
              duration={compositorDuration}
              frameRate={compositorFps ?? 30}
              currentTime={videoState?.currentTime ?? 0}
              isPlaying={videoState !== undefined ? !videoState.paused : undefined}
              onPlayingChange={handlePlayingChange}
              onSeek={handleSeek}
              tracks={timelineTracks}
              selectedTrackId={selectedLayerId ?? null}
              onTrackLockToggle={handleTrackLockToggle}
              onTrackMuteToggle={handleTrackMuteToggle}
              onTrackVisibilityToggle={handleTrackVisibilityToggle}
              onTrackStartTimeChange={handleTrackStartTimeChange}
              onTrackTrimChange={handleTrackTrimChange}
              onTrackSelect={handleTrackSelect}
              onTrackReorder={handleTrackReorder}
            >
              <PlaybackControls
                isMuted={videoState?.muted}
                onToggleMute={handleToggleMute}
                canvasMode={canvasMode}
                onCanvasModeChange={handleCanvasModeChange}
                canvasZoom={canvasZoom}
                onCanvasZoomChanged={onCanvasZoomChanged}
                onZoomToFit={onZoomToFit}
              />
              <Timeline />
            </TimelineProvider>
          </Box>
        </>
      )}
    </FlexCol>
  );
}
